import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, Popconfirm, Popover } from 'antd';
import { LinkOutlined } from '@ant-design/icons';
import { SketchPicker } from 'react-color';
import { shortenString } from '../../../../helpers';
import { ReactComponent as RectangleIcon } from '../../../../svg/graphicEditor/rectangle-small.svg';
import { ReactComponent as EllipseIcon } from '../../../../svg/graphicEditor/ellipse-small.svg';
import { ReactComponent as PolygonIcon } from '../../../../svg/graphicEditor/polygon-small.svg';
import { ReactComponent as StarIcon } from '../../../../svg/graphicEditor/star-small.svg';
import { ReactComponent as LineIcon } from '../../../../svg/graphicEditor/line-small.svg';
import { ReactComponent as TextIcon } from '../../../../svg/graphicEditor/text-small.svg';
import { ReactComponent as ImageIcon } from '../../../../svg/graphicEditor/image-small.svg';
import { ReactComponent as EditIcon } from '../../../../svg/graphicEditor/edit-small.svg';
import { ReactComponent as CopyIcon } from '../../../../svg/graphicEditor/copy-small.svg';
import { ReactComponent as DeleteIcon } from '../../../../svg/graphicEditor/delete-small.svg';
import { ReactComponent as EyeIcon } from '../../../../svg/graphicEditor/eye-small.svg';
import { ReactComponent as EyeClosedIcon } from '../../../../svg/graphicEditor/eye-closed-small.svg';
import { ReactComponent as DownIcon } from '../../../../svg/icon/down-arrow.svg';

import styles from './GraphicLayersPanel.module.css';

function sketchColorToCss(c) {
  if (!c?.rgb) return c?.hex ?? '';
  const { r, g, b, a } = c.rgb;
  if (a !== undefined && a < 1) {
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  }
  return c.hex;
}

export const LayerElement = React.memo((props) => {
  const item = props.item;
  const isSelected = props.isSelected;
  const isAttachLinked = !!item.isAttachLinked;
  const [name, setName] = useState(item?.title);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState();
  const [layerColorPopoverOpen, setLayerColorPopoverOpen] = useState(false);
  const [attachLinkPopoverOpen, setAttachLinkPopoverOpen] = useState(false);
  const ref = useRef();
  let timer;

  useEffect(() => {
    setName(item?.title);
  }, [item]);

  useEffect(() => {
    if (isEditing && ref.current) {
      ref.current.focus();
      if (editName === item.defaultName) {
        ref.current.select();
      }
    }
  }, [ref.current, isEditing]);

  const getElementIcon = (type) => {
    const iconsByType = {
      rect: <RectangleIcon />,
      ellipse: <EllipseIcon />,
      polygon: <PolygonIcon />,
      star: <StarIcon />,
      line: <LineIcon />,
      image: <ImageIcon />,
      text: <TextIcon />,
      default: null,
    };
    return iconsByType[type] ?? iconsByType.default;
  };

  const editElementName = (e) => {
    setEditName(item.title);
    setIsEditing(true);
    e.stopPropagation();
  };

  const endEditElementName = (cancel = false) => {
    if (editName && !cancel && editName !== item.defaultName) {
      item.title = editName;
      setName(item.title);

      if (props.onNameChanged) {
        props.onNameChanged(item.key, item.title);
      }
    }
    setEditName('');
    setIsEditing(false);
  };

  const onKeyDown = (e) => {
    if (e.keyCode === 27) {
      e.preventDefault();
      endEditElementName(true);
    }
    if (e.keyCode === 13) {
      e.preventDefault();
      endEditElementName();
    }
  };

  const copyElement = (e) => {
    if (props.onCopyElement) {
      props.onCopyElement(item);
    }
    e.stopPropagation();
  };

  const deleteElement = (e) => {
    if (props.onDeleteElement) {
      props.onDeleteElement(item);
    }
    e.stopPropagation();
  };

  const selectElement = (e) => {
    if (props.onSelectElement) {
      props.onSelectElement(e, item.key);
    }
  };

  const hideElement = (e) => {
    e.stopPropagation();
    props.onElementHide(item);
  };

  const toggleCollapse = (e, key, isCollapsed) => {
    e.stopPropagation();
    props.collapseToggle(key, isCollapsed);
  };

  const onClickHandler = (e) => {
    clearTimeout(timer);
    if (e.detail === 1) {
      const event = { ...e };
      timer = setTimeout(() => {
        selectElement(event);
      }, 200);
    } else if (e.detail === 2) {
      editElementName(e);
    }
    e.stopPropagation();
  };

  const onLayerColorComplete = (c) => {
    const css = sketchColorToCss(c);
    if (props.onLayerLabelColorChanged && css) {
      props.onLayerLabelColorChanged(item.key, css);
    }
  };

  const clearLayerHighlight = (e) => {
    e.stopPropagation();
    if (props.onLayerLabelColorChanged) {
      props.onLayerLabelColorChanged(item.key, '');
    }
    setLayerColorPopoverOpen(false);
  };

  const onAttachLinkColorComplete = (c) => {
    const css = sketchColorToCss(c);
    if (props.onAttachLinkIconColorChanged && css) {
      props.onAttachLinkIconColorChanged(item.key, css);
    }
  };

  const clearAttachLinkColor = (e) => {
    e.stopPropagation();
    if (props.onAttachLinkIconColorChanged) {
      props.onAttachLinkIconColorChanged(item.key, '');
    }
    setAttachLinkPopoverOpen(false);
  };

  const layerLabel = item.layerLabelColor;
  const attachLinkIconColor = item.attachLinkIconColor;

  const popoverMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const layerColorPopoverContent = (
    <div
      className={styles.layerColorPopover}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={popoverMouseDown}
    >
      <SketchPicker
        color={layerLabel || '#888888'}
        onChangeComplete={onLayerColorComplete}
      />
      <Button type="default" size="small" block onClick={clearLayerHighlight}>
        Clear highlight
      </Button>
    </div>
  );

  const attachLinkPopoverContent = isAttachLinked ? (
    <div
      className={styles.layerColorPopover}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={popoverMouseDown}
    >
      <SketchPicker
        color={attachLinkIconColor || '#888888'}
        onChangeComplete={onAttachLinkColorComplete}
      />
      <Button type="default" size="small" block onClick={clearAttachLinkColor}>
        Clear link color
      </Button>
    </div>
  ) : null;

  return (
    <div
      id={props.item._id}
      onClick={selectElement}
      className={`
                        ${styles.layersElement}
                        ${isSelected || props.selectedByRelative ? styles.selectedElement : ''}
                        ${isSelected && props.selectedByRelative ? styles.selectedElementInGroup : ''}
                        ${item.parentKey ? styles.childrenElement : ''}
                        ${item.collapsed ? styles.elementCollapsed : ''}
                      `}
      style={
        layerLabel ? { borderLeft: `3px solid ${layerLabel}` } : undefined
      }
    >
      <div className={styles.iconBlock}>
        {getElementIcon(item.elementType)}
        {!!item?.children.length && (
          <Button
            icon={
              <DownIcon
                className={`${styles.arrowDown} ${item.parentCollapsed ? styles.arrowUp : ''}`}
              />
            }
            shape="circle"
            type="text"
            onClick={(e) => toggleCollapse(e, item.key, !item.parentCollapsed)}
          />
        )}
      </div>

      <div className={styles.elementBlock}>
        <div className={styles.nameRow}>
          <Popover
            open={layerColorPopoverOpen}
            onOpenChange={setLayerColorPopoverOpen}
            trigger="click"
            placement="bottomLeft"
            title="Layer highlight"
            content={layerColorPopoverContent}
            getPopupContainer={() => document.body}
          >
            <button
              type="button"
              className={`${styles.layerColorSwatch} ${layerLabel ? '' : styles.layerColorSwatchEmpty}`}
              style={layerLabel ? { backgroundColor: layerLabel } : undefined}
              aria-label="Layer highlight color"
              onClick={(e) => e.stopPropagation()}
            />
          </Popover>

          {isAttachLinked && (
            <Popover
              open={attachLinkPopoverOpen}
              onOpenChange={setAttachLinkPopoverOpen}
              trigger="click"
              placement="bottomLeft"
              title="Relative link"
              content={attachLinkPopoverContent}
              getPopupContainer={() => document.body}
            >
              <button
                type="button"
                className={styles.layerLinkIconButton}
                aria-label="Relative link color"
                onClick={(e) => e.stopPropagation()}
              >
                <LinkOutlined
                  className={styles.layerChainIcon}
                  style={
                    attachLinkIconColor ? { color: attachLinkIconColor } : undefined
                  }
                />
              </button>
            </Popover>
          )}

          <div className={styles.nameElement}>
            {!isEditing && (
              <div onClick={onClickHandler}>
                {name && shortenString(name, 15)}
              </div>
            )}
            {isEditing && (
              <Input
                onClick={(e) => {
                  e.stopPropagation();
                }}
                ref={ref}
                onKeyDown={onKeyDown}
                value={editName}
                size="small"
                onChange={(e) => {
                  setEditName(e.target.value);
                }}
                onBlur={() => endEditElementName(false)}
                className={styles.input}
              />
            )}
          </div>
        </div>
        <div className={styles.buttonsBlock}>
          {!isEditing && (
            <Button
              type="text"
              icon={<EditIcon />}
              onClick={editElementName}
              className={`${styles.editButton} ${styles.sideButton}`}
            />
          )}
          <Button
            type="text"
            icon={item.field.attrs.opacity === 0 ? <EyeClosedIcon /> : <EyeIcon />}
            className={styles.sideButton}
            onClick={hideElement}
          />
          <Button
            type="text"
            icon={<CopyIcon />}
            onClick={copyElement}
            className={styles.sideButton}
          />
          <Popconfirm
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            title={`Delete element "${item.title}"?`}
            onConfirm={deleteElement}
            okText="Delete"
            cancelText="Cancel"
          >
            <Button type="text" icon={<DeleteIcon />} className={styles.sideButton} />
          </Popconfirm>
        </div>
      </div>
    </div>
  );
});
